/** Row-shaping for the Privilege Report export.
 *
 *  Pure, dependency-free shaping of the resolver payload (#223) into the seven
 *  workbook sheets. Both the XLSX and the JSON export derive from
 *  buildReportSheets(), so the two formats reflect the same organization and
 *  cannot drift. Sheet names use the project glossary (Users / UserPrivileges),
 *  never "Members". The Groups sheet carries parent_group_id; the parent link is
 *  org structure, NOT a grant path (subgroups do not inherit) — see
 *  GROUPS_PARENT_LEGEND.
 */

export const GROUPS_PARENT_LEGEND =
  "Legend: Parent Group ID is org structure only, NOT a grant path — subgroups do NOT inherit a parent group's privileges. Owner Unique IDs shown as 'Patient-owned #N' are per-report surrogates and are NOT stable across runs.";

const arr = (v) => (Array.isArray(v) ? v : []);
const blank = (v) => (v === null || v === undefined ? "" : v);

function buildUsersRows(reportData) {
  return arr(reportData?.users).map((u) => ({
    "Unique ID": blank(u?.uniqueId),
    Username: blank(u?.username),
    "Account Type": blank(u?.accountType),
    "First Name": blank(u?.firstName),
    "Last Name": blank(u?.lastName),
    "Display Name": blank(u?.displayName),
    Email: blank(u?.email),
  }));
}

function buildGroupsRows(reportData) {
  return arr(reportData?.groups).map((g) => ({
    "Group ID": blank(g?.groupId),
    Name: blank(g?.name),
    "Parent Group ID": blank(g?.parentGroupId),
    "Owner Unique ID": blank(g?.ownerUniqueId),
  }));
}

function buildMembershipsRows(reportData) {
  const userByUid = new Map(arr(reportData?.users).map((u) => [u?.uniqueId, u]));
  return arr(reportData?.memberships).map((m) => ({
    "Group ID": blank(m?.groupId),
    "User Unique ID": blank(m?.uniqueId),
    Username: blank(userByUid.get(m?.uniqueId)?.username),
    "Assigned At": blank(m?.assignedAt),
  }));
}

function buildPrivilegesRows(reportData) {
  return arr(reportData?.privileges).map((p) => ({
    "Privilege ID": blank(p?.id),
    Code: blank(p?.code),
    Label: blank(p?.label),
    Category: blank(p?.category),
    Description: blank(p?.description),
    Risk: blank(p?.risk),
  }));
}

function buildGroupPrivilegesRows(reportData) {
  const groupById = new Map(arr(reportData?.groups).map((g) => [g?.groupId, g]));
  const privById = new Map(arr(reportData?.privileges).map((p) => [p?.id, p]));
  const riskRank = (r) => (typeof r === "number" ? r : -1);
  return arr(reportData?.groupPrivileges)
    .map((gp) => ({
      Risk: blank(privById.get(gp?.privilegeId)?.risk),
      "Group ID": blank(gp?.groupId),
      "Group Name": blank(groupById.get(gp?.groupId)?.name),
      "Privilege ID": blank(gp?.privilegeId),
      "Privilege Code": blank(gp?.privilegeCode),
      "Privilege Label": blank(gp?.privilegeLabel),
    }))
    .sort(
      (a, b) =>
        String(a["Group Name"]).localeCompare(String(b["Group Name"])) ||
        String(a["Group ID"]).localeCompare(String(b["Group ID"])) ||
        riskRank(b.Risk) - riskRank(a.Risk) ||
        String(a["Privilege Code"]).localeCompare(String(b["Privilege Code"]))
    );
}

function buildUserPrivilegesRows(reportData) {
  const userByUid = new Map(arr(reportData?.users).map((u) => [u?.uniqueId, u]));
  return arr(reportData?.userPrivileges).map((up) => ({
    "User Unique ID": blank(up?.uniqueId),
    Username: blank(userByUid.get(up?.uniqueId)?.username),
    "Privilege ID": blank(up?.privilegeId),
    "Privilege Code": blank(up?.privilegeCode),
    Mode: blank(up?.mode),
  }));
}

/* Port: the kinds of grant a `grantingGroups` entry can be. Main grants only
 * through groups; the port also through roles and an organization-wide
 * emergency (break-glass) incident, and the bridge folds those into
 * `grantingGroups` (with these kinds) so the Source column names them. An
 * entry without a kind is a group, as every one of main's is. */
export const SOURCE_GROUP = "group";
export const SOURCE_ROLE = "role";
export const SOURCE_BREAK_GLASS = "break_glass";

const KIND_ORDER = [SOURCE_GROUP, SOURCE_ROLE, SOURCE_BREAK_GLASS];
const KIND_STATUS = {
  [SOURCE_GROUP]: "INHERITED",
  [SOURCE_ROLE]: "GRANTED (role)",
  [SOURCE_BREAK_GLASS]: "GRANTED (break-glass)",
};
const KIND_METHOD = {
  [SOURCE_GROUP]: "Inherited from Group",
  [SOURCE_ROLE]: "Granted by Role",
  [SOURCE_BREAK_GLASS]: "Emergency Access (break-glass)",
};

/** The kinds of grant behind one effective row, in a fixed order; a group
 *  when nothing says otherwise (main's rows, and a row naming no source). */
export function grantKinds(e) {
  const present = new Set(
    arr(e?.grantingGroups).map((x) => (KIND_ORDER.includes(x?.kind) ? x.kind : SOURCE_GROUP))
  );
  const kinds = KIND_ORDER.filter((k) => present.has(k));
  return kinds.length ? kinds : [SOURCE_GROUP];
}

/** Status/source of one effective row: overrides beat group grants. A row
 *  held through more than one kind of grant names each ("INHERITED; GRANTED
 *  (role)"), so the compliance artifact never states one path as the only one. */
export function effectiveStatus(e) {
  if (e?.override === "deny") {
    return { status: "DENIED (override)", source: "personal deny" };
  }
  if (e?.override === "grant") {
    return { status: "GRANTED (override)", source: "personal grant" };
  }
  const groups = arr(e?.grantingGroups)
    .map((x) => x?.name)
    .filter(Boolean)
    .join("; ");
  return { status: grantKinds(e).map((k) => KIND_STATUS[k]).join("; "), source: groups };
}

/** Stakeholder wording for how a privilege was received (replaces Status on
 *  the merged All Users tab). Source: group names (and, in the port, role and
 *  incident names), or blank for explicit grants/denies — the Method already
 *  says it was personal. */
export function effectiveMethod(e) {
  if (e?.override === "deny") {
    return { method: "Explicitly Denied", source: "" };
  }
  if (e?.override === "grant") {
    return { method: "Explicitly Granted", source: "" };
  }
  const groups = arr(e?.grantingGroups)
    .map((x) => x?.name)
    .filter(Boolean)
    .join("; ");
  return { method: grantKinds(e).map((k) => KIND_METHOD[k]).join("; "), source: groups };
}

/** The merged workbook tab (staff + patients), one row per user × privilege.
 *  Patients keep a minimal identity slice: username only, no display name.
 *  Sorted into per-user blocks (username, then unique id); within a block,
 *  Unexpected Access first, then riskiest first, then code. */
export function buildAllUsersRows(reportData) {
  const userByUid = new Map(arr(reportData?.users).map((u) => [u?.uniqueId, u]));
  const privById = new Map(arr(reportData?.privileges).map((p) => [p?.id, p]));

  const shape = (e, identity, unexpected) => {
    const priv = privById.get(e?.privilegeId);
    const { method, source } = effectiveMethod(e);
    return {
      Risk: blank(e?.risk),
      "User Unique ID": blank(e?.uniqueId),
      Username: identity.username,
      "Display Name": identity.displayName,
      "Account Type": identity.accountType,
      "Privilege ID": blank(e?.privilegeId),
      "Privilege Code": blank(e?.privilegeCode),
      "Privilege Label": blank(priv?.label),
      Category: blank(priv?.category),
      Method: method,
      Source: source,
      "Unexpected Access": unexpected,
    };
  };

  const staff = arr(reportData?.effective).map((e) => {
    const u = userByUid.get(e?.uniqueId);
    return shape(e, { username: blank(u?.username), displayName: blank(u?.displayName), accountType: blank(u?.accountType) }, "");
  });
  const patients = arr(reportData?.patientPrivileges?.rows).map((e) =>
    shape(e, { username: blank(e?.username), displayName: "", accountType: "Patient" }, yesNo(e?.beyondBaseline))
  );

  const riskRank = (r) => (typeof r === "number" ? r : -1);
  return [...staff, ...patients].sort(
    (a, b) =>
      String(a.Username).localeCompare(String(b.Username)) ||
      String(a["User Unique ID"]).localeCompare(String(b["User Unique ID"])) ||
      (a["Unexpected Access"] === "yes" ? 0 : 1) - (b["Unexpected Access"] === "yes" ? 0 : 1) ||
      riskRank(b.Risk) - riskRank(a.Risk) ||
      String(a["Privilege Code"]).localeCompare(String(b["Privilege Code"]))
  );
}

function buildEffectiveRows(reportData) {
  const userByUid = new Map(arr(reportData?.users).map((u) => [u?.uniqueId, u]));
  const privById = new Map(arr(reportData?.privileges).map((p) => [p?.id, p]));
  return arr(reportData?.effective).map((e) => {
    const user = userByUid.get(e?.uniqueId);
    const priv = privById.get(e?.privilegeId);
    const { status, source } = effectiveStatus(e);
    return {
      "User Unique ID": blank(e?.uniqueId),
      "Display Name": blank(user?.displayName),
      "Account Type": blank(user?.accountType),
      "Privilege ID": blank(e?.privilegeId),
      "Privilege Code": blank(e?.privilegeCode),
      "Privilege Label": blank(priv?.label),
      Category: blank(priv?.category),
      Status: status,
      Source: source,
      Risk: blank(e?.risk),
    };
  });
}

const yesNo = (v) => (v ? "yes" : "no");

// The ONE sheet where patients appear — identified by Unique ID +
// Username only (minimum-necessary slice; the app is the lookup surface).
// Beyond-Baseline rows lead so the remediation list needs no scrolling.
function buildPatientPrivilegesRows(reportData) {
  const privById = new Map(arr(reportData?.privileges).map((p) => [p?.id, p]));
  const rows = arr(reportData?.patientPrivileges?.rows).map((e) => {
    const priv = privById.get(e?.privilegeId);
    const { status, source } = effectiveStatus(e);
    return {
      "Patient Unique ID": blank(e?.uniqueId),
      Username: blank(e?.username),
      "Privilege ID": blank(e?.privilegeId),
      "Privilege Code": blank(e?.privilegeCode),
      "Privilege Label": blank(priv?.label),
      Category: blank(priv?.category),
      Status: status,
      Source: source,
      Risk: blank(e?.risk),
      "Unexpected Access": yesNo(e?.beyondBaseline),
    };
  });
  return rows.sort(
    (a, b) =>
      (a["Unexpected Access"] === "yes" ? 0 : 1) - (b["Unexpected Access"] === "yes" ? 0 : 1) ||
      String(a["Patient Unique ID"]).localeCompare(String(b["Patient Unique ID"])) ||
      String(a["Privilege Code"]).localeCompare(String(b["Privilege Code"]))
  );
}

function buildRiskPrivilegedUsersRows(reportData) {
  return arr(reportData?.riskClassification?.riskPrivilegedUsers).map((u) => ({
    "User Unique ID": blank(u?.uniqueId),
    "Display Name": blank(u?.displayName),
    "Risk Score Total": blank(u?.riskScoreTotal),
    "High-Risk (risk 2) Count": blank(u?.highRiskCount),
  }));
}

function buildRiskPrivilegedGroupsRows(reportData) {
  return arr(reportData?.riskClassification?.riskPrivilegedGroups).map((g) => ({
    "Group ID": blank(g?.groupId),
    Name: blank(g?.name),
    "Risk Score Total": blank(g?.riskScoreTotal),
    "High-Risk (risk 2) Count": blank(g?.highRiskCount),
  }));
}

// Canonical column order per sheet — must match the row shape each builder
// emits (the specs assert both, so drift fails tests). Lets consumers render a
// header row even when a sheet has no data rows.
export const SHEET_HEADERS = {
  Users: ["Unique ID", "Username", "Account Type", "First Name", "Last Name", "Display Name", "Email"],
  Groups: ["Group ID", "Name", "Parent Group ID", "Owner Unique ID"],
  Memberships: ["Group ID", "User Unique ID", "Username", "Assigned At"],
  Privileges: ["Privilege ID", "Code", "Label", "Category", "Description", "Risk"],
  GroupPrivileges: ["Risk", "Group ID", "Group Name", "Privilege ID", "Privilege Code", "Privilege Label"],
  UserPrivileges: ["User Unique ID", "Username", "Privilege ID", "Privilege Code", "Mode"],
  "Effective Privileges": [
    "User Unique ID",
    "Display Name",
    "Account Type",
    "Privilege ID",
    "Privilege Code",
    "Privilege Label",
    "Category",
    "Status",
    "Source",
    "Risk",
  ],
  "Patient Privileges": [
    "Patient Unique ID",
    "Username",
    "Privilege ID",
    "Privilege Code",
    "Privilege Label",
    "Category",
    "Status",
    "Source",
    "Risk",
    "Unexpected Access",
  ],
  // Workbook-only merged tab (staff + patients); not part of the JSON mirror,
  // which keeps the normalized collections above.
  "All Users": [
    "Risk",
    "User Unique ID",
    "Username",
    "Display Name",
    "Account Type",
    "Privilege ID",
    "Privilege Code",
    "Privilege Label",
    "Category",
    "Method",
    "Source",
    "Unexpected Access",
  ],
  "Risk-Privileged Users": ["User Unique ID", "Display Name", "Risk Score Total", "High-Risk (risk 2) Count"],
  "Risk-Privileged Groups": ["Group ID", "Name", "Risk Score Total", "High-Risk (risk 2) Count"],
};

/**
 * The workbook sheets, in order (seven collection sheets + the two risk
 * outputs), each `{ name, headers, rows }`. Single source for both the XLSX
 * tabs and the JSON mirror.
 * @param {object|null} reportData the resolver payload (#223)
 * @returns {Array<{name: string, headers: Array<string>, rows: Array<object>}>}
 */
export function buildReportSheets(reportData) {
  const sheets = [
    { name: "Users", rows: buildUsersRows(reportData) },
    { name: "Groups", rows: buildGroupsRows(reportData) },
    { name: "Memberships", rows: buildMembershipsRows(reportData) },
    { name: "Privileges", rows: buildPrivilegesRows(reportData) },
    { name: "GroupPrivileges", rows: buildGroupPrivilegesRows(reportData) },
    { name: "UserPrivileges", rows: buildUserPrivilegesRows(reportData) },
    { name: "Effective Privileges", rows: buildEffectiveRows(reportData) },
    // Patients, quarantined to this one sheet.
    { name: "Patient Privileges", rows: buildPatientPrivilegesRows(reportData) },
    // The risk-classification story outputs (#226), ranked by the resolver.
    // Risk-privileged is a sortable measure — risk 0..2 (higher = riskier) sums
    // directly into the total, plus a high-risk (risk 2) count — NOT a boolean.
    { name: "Risk-Privileged Users", rows: buildRiskPrivilegedUsersRows(reportData) },
    { name: "Risk-Privileged Groups", rows: buildRiskPrivilegedGroupsRows(reportData) },
  ];
  return sheets.map((s) => ({ ...s, headers: SHEET_HEADERS[s.name] }));
}
